import { Component, inject, input, OnInit, output, signal, viewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import {
  getCreatePersonnelFileEntryDtoFormGroup,
  getUpdatePersonnelFileEntryDtoFormGroup,
  WorkCreatePersonnelFileEntryDto,
  WorkPersonnelFileCategoriesV2Service,
  WorkPersonnelFileEntry,
  WorkPersonnelFilesV2Service,
  WorkUpdatePersonnelFileEntryDto,
} from '@work-frontend-monorepo/work-rest-api';
import { AlertDialogService, ModalComponent } from '@work-frontend-monorepo/work-ui/modals';
import {
  InputDatepickerComponent,
  InputFieldComponent,
  SelectComponent,
  SelectDropdownOptions,
} from '@work-frontend-monorepo/work-ui/forms';
import { ButtonComponent } from '@work-frontend-monorepo/work-ui/layout';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { LoadingAndFeedbackState } from '@work-frontend-monorepo/work-ui/utilities';

@Component({
  selector: 'sfg-app-personaldossier-create-update-modal',
  templateUrl: './personaldossier-create-update-modal.component.html',
  styleUrls: ['./personaldossier-create-update-modal.component.scss'],
  standalone: true,
  imports: [
    ModalComponent,
    ReactiveFormsModule,
    SelectComponent,
    InputDatepickerComponent,
    InputFieldComponent,
    ButtonComponent,
    TranslatePipe,
  ],
})
export class PersonaldossierCreateUpdateModalComponent implements OnInit {
  userId = input.required<number>();
  entryEdited = output<void>();

  modal = viewChild<ModalComponent>('personaldossierModal');

  entryToUpdate = signal<WorkPersonnelFileEntry | undefined>(undefined);
  formGroupCreate = getCreatePersonnelFileEntryDtoFormGroup();
  formGroupUpdate = getUpdatePersonnelFileEntryDtoFormGroup();
  formSaveLoadingState: LoadingAndFeedbackState = 'none';
  categoryOptions = signal<SelectDropdownOptions[]>([]);

  private readonly personnelFilesService = inject(WorkPersonnelFilesV2Service);
  private readonly personnelFileCategoriesService = inject(WorkPersonnelFileCategoriesV2Service);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly alertDialogService = inject(AlertDialogService);

  async ngOnInit() {
    await this.loadCategories();
  }

  async openModal(entry?: WorkPersonnelFileEntry) {
    this.resetForm();
    this.entryToUpdate.set(entry);
    if (entry) {
      this.formGroupUpdate.patchValue({
        categoryId: entry.categoryId,
        subject: entry.subject,
        notes: entry.notes,
        entryDate: entry.entryDate,
      });
    } else {
      this.formGroupCreate.patchValue({
        userId: this.userId(),
        entryDate: new Date().toISOString(),
      });
    }
    this.modal()?.open();
  }

  protected async createEntry() {
    try {
      this.formSaveLoadingState = 'loading';
      await lastValueFrom(
        this.personnelFilesService.personnelFilesV2ControllerCreate({
          body: this.formGroupCreate.value as WorkCreatePersonnelFileEntryDto,
        }),
      );
      this.formSaveLoadingState = 'success';
      this.entryEdited.emit();
      this.alertDialogService.showAlert({ title: 'ERSTELLEN_ERFOLGREICH', feedbackState: 'success' });
      this.modal()?.close();
    } catch (createEntryException) {
      this.formSaveLoadingState = 'error';
      this.exceptionHandlingService.handleExceptionWithAlert('createPersonaldossierEntryException', createEntryException);
    }
  }

  protected async updateEntry() {
    const entry = this.entryToUpdate();
    if (!entry) return;
    try {
      this.formSaveLoadingState = 'loading';
      await lastValueFrom(
        this.personnelFilesService.personnelFilesV2ControllerUpdate({
          id: entry.id,
          body: this.formGroupUpdate.value as WorkUpdatePersonnelFileEntryDto,
        }),
      );
      this.formSaveLoadingState = 'success';
      this.entryEdited.emit();
      this.modal()?.close();
    } catch (updateEntryException) {
      this.formSaveLoadingState = 'error';
      this.exceptionHandlingService.handleExceptionWithAlert('updatePersonaldossierEntryException', updateEntryException);
    }
  }

  private async loadCategories() {
    try {
      const options = await lastValueFrom(
        this.personnelFileCategoriesService.personnelFileCategoriesV2ControllerGetDropdownOptions(),
      );
      this.categoryOptions.set(options ?? []);
    } catch (loadCategoriesException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadPersonaldossierKategorienException', loadCategoriesException);
    }
  }

  private resetForm() {
    this.formGroupCreate = getCreatePersonnelFileEntryDtoFormGroup();
    this.formGroupUpdate = getUpdatePersonnelFileEntryDtoFormGroup();
    this.entryToUpdate.set(undefined);
    this.formSaveLoadingState = 'none';
  }
}
